// Phase 11 Step 12 — OwlMorningReveal.
//
// Morning result screen — the core emotional payoff of the Owl Hour.
// Designed for the morning verified-result update.
//
// Win: Playfair italic greeting → Bebas Neue "WON" (cyan) →
//      mono return amount → leg-by-leg results → share card + CTA.
// Loss: Subdued greeting → "DID NOT LAND" → which legs hit →
//       educational debrief → "Place Tonight's Owl Hour" CTA.
// Partial: "SO CLOSE" (violet) → X of Y legs → near-miss framing.

import { useState } from "react";
import type { OwlUserSlip } from "../../owl/useOwlHourUserSlips.ts";
import type { OwlHourSlip } from "../../owl/useOwlHourSlips.ts";
import { OWL_TIER_META } from "../../owl/useOwlHourSlips.ts";
import { OwlShareCard } from "./OwlShareCard.tsx";
import * as s from "./owl.styles.ts";

// ─── Formatters ───────────────────────────────────────────────

function fmtCurrency(n: number): string {
  return `GH₵${n.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(raw: string): string {
  if (raw.length === 0) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Component ────────────────────────────────────────────────

export interface OwlMorningRevealProps {
  userSlip: OwlUserSlip;
  slip: OwlHourSlip;
}

export function OwlMorningReveal({
  userSlip,
  slip,
}: OwlMorningRevealProps): JSX.Element {
  const [showShare, setShowShare] = useState(false);

  const meta = OWL_TIER_META[slip.tier];
  const result = userSlip.result;
  const isWin = result === "win";
  const isPartial = result === "partial";
  const legsCorrect = userSlip.legs_correct ?? 0;
  const legsTotal = userSlip.legs_total ?? slip.legs.length;
  const returnAmt = userSlip.actual_return ?? userSlip.potential_return ?? 0;

  // Greeting copy
  let greeting: string;
  let resultText: string;
  let resultStyle = s.revealResultWin;

  if (isWin) {
    greeting = "Good morning.";
    resultText = "WON";
    resultStyle = s.revealResultWin;
  } else if (isPartial) {
    greeting = "So close.";
    resultText = "SO CLOSE";
    resultStyle = s.revealResultPartial;
  } else {
    greeting = "Morning.";
    resultText = "DID NOT LAND";
    resultStyle = s.revealResultLoss;
  }

  return (
    <div style={s.revealCard}>
      {/* ── Header ──────────────────────────────────── */}
      <div style={s.revealHeader}>
        <span style={{ fontSize: "32px", lineHeight: 1 }}>🦉</span>
        <span style={s.revealGreeting}>{greeting}</span>
        <span style={resultStyle}>{resultText}</span>

        {isWin ? (
          <>
            <span style={s.revealReturnAmount}>{fmtCurrency(returnAmt)}</span>
            <span style={s.revealStake}>
              Stake: {fmtCurrency(userSlip.stake)} → Return: {fmtCurrency(returnAmt)}
            </span>
          </>
        ) : null}

        {isPartial ? (
          <span style={s.revealStake}>
            {legsCorrect} of {legsTotal} legs correct — missed by{" "}
            {legsTotal - legsCorrect}
          </span>
        ) : null}

        <span style={s.revealOdds}>
          {meta.label} · {slip.combined_odds !== null ? `${slip.combined_odds.toFixed(1)}x` : ""}
        </span>
        <span style={{
          fontFamily: "var(--sz-font-body)",
          fontSize: "var(--sz-body-sm)",
          color: "var(--owl-text-3)",
        }}>
          {fmtDate(slip.slip_date)}
        </span>
      </div>

      {/* ── Leg-by-leg results ──────────────────────── */}
      <div style={s.revealBody}>
        <span style={s.revealSectionTitle}>Leg by Leg</span>

        {slip.legs.map((leg, i) => {
          const legResult = leg.result;
          const isLegWin = legResult === "win";
          const isLegLoss = legResult === "loss";

          return (
            <div key={leg.fixture_id} style={s.revealLegRow}>
              {/* Result icon */}
              <span style={{
                ...s.revealLegIcon,
                ...(isLegWin ? s.revealLegWin : isLegLoss ? s.revealLegLoss : s.revealLegPending),
              }}>
                {isLegWin ? "✓" : isLegLoss ? "✗" : "·"}
              </span>

              {/* Match + detail */}
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span style={{
                  ...s.revealLegMatch,
                  ...(isLegLoss ? { opacity: 0.6 } : {}),
                }}>
                  {leg.market}: {leg.selection}
                </span>
                <span style={s.revealLegDetail}>
                  {leg.home} vs {leg.away} · {leg.league_short}
                </span>
                {leg.reasoning.length > 0 ? (
                  <span style={{
                    ...s.revealLegDetail,
                    fontStyle: "italic",
                    marginTop: "2px",
                  }}>
                    {leg.reasoning}
                  </span>
                ) : null}
              </div>

              {/* Result badge */}
              <span style={{
                ...s.revealLegResult,
                ...(isLegWin ? s.revealLegWin : isLegLoss ? s.revealLegLoss : s.revealLegPending),
              }}>
                {isLegWin ? "WON" : isLegLoss ? "LOST" : "PENDING"}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Footer CTAs ─────────────────────────────── */}
      <div style={s.revealFooter}>
        {isWin ? (
          <>
            <button
              type="button"
              style={s.shareBtn}
              onClick={() => setShowShare((v) => !v)}
            >
              {showShare ? "Hide Share Card" : "Share This Win 🦉"}
            </button>
            <button type="button" style={s.secondaryBtn}>
              Tonight's Owl Hour
            </button>
          </>
        ) : (
          <>
            <button type="button" style={s.primaryBtn}>
              Place Tonight's Owl Hour
            </button>
            <button type="button" style={s.secondaryBtn}>
              View History
            </button>
          </>
        )}
      </div>

      {/* ── Share card (toggled) ─────────────────────── */}
      {showShare && isWin ? (
        <div style={{ padding: "var(--sz-space-5)" }}>
          <OwlShareCard userSlip={userSlip} slip={slip} />
        </div>
      ) : null}

      {/* ── Loss debrief ────────────────────────────── */}
      {!isWin && !isPartial ? (
        <div style={{
          padding: "var(--sz-space-4) var(--sz-space-5) var(--sz-space-5)",
        }}>
          <span style={s.disclosureBanner}>
            {legsCorrect} of {legsTotal} legs came through.
            The agents will analyse tonight's late-night slate and
            prepare the next overnight selections.
          </span>
        </div>
      ) : null}

      {/* ── Partial near-miss ───────────────────────── */}
      {isPartial ? (
        <div style={{
          padding: "var(--sz-space-4) var(--sz-space-5) var(--sz-space-5)",
        }}>
          <span style={s.disclosureBanner}>
            Your {meta.label} missed by {legsTotal - legsCorrect}{" "}
            {legsTotal - legsCorrect === 1 ? "leg" : "legs"}.{" "}
            {legsCorrect} of {legsTotal} correct. The agents
            are already analysing tonight's fixtures.
          </span>
        </div>
      ) : null}
    </div>
  );
}
